import { Markdown } from '@/components/chat/Markdown';
import { api } from '@/lib/api';
import type { WikiDocumentStatus } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  Code2,
  Copy,
  Download,
  Eye,
  FileText,
  RotateCcw,
} from 'lucide-react';
import { useState } from 'react';

const STUCK_THRESHOLD_MS = 5 * 60 * 1000; // 5분 이상 진행 없으면 stuck 의심.

const STATUS_LABEL: Record<WikiDocumentStatus, string> = {
  uploaded: '업로드됨',
  parsing: '파싱 중',
  parsed: '파싱 완료',
  digesting: 'LLM 정리 중',
  done: '완료',
  failed: '실패',
  deleted: '외부 삭제됨',
  archived: '보관됨',
};

const STATUS_TONE: Record<WikiDocumentStatus, string> = {
  uploaded: 'bg-foreground/10 text-foreground-muted',
  parsing: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  parsed: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  digesting: 'bg-accent/10 text-accent',
  done: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  failed: 'bg-destructive/10 text-destructive',
  deleted: 'bg-foreground/10 text-foreground-faint line-through',
  archived: 'bg-foreground/10 text-foreground-muted',
};

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

interface Props {
  documentId: string;
  /** admin 모드일 때 추가 액션 (재처리/삭제) 노출. */
  isAdmin?: boolean;
  /** 뒤로 가기 링크 — 사용자/admin 별로 다름. */
  backTo: '/wikis/documents' | '/admin/wikis/documents';
}

export function WikiDocumentPreview({ documentId, isAdmin = false, backTo }: Props) {
  const queryClient = useQueryClient();
  const [view, setView] = useState<'rendered' | 'raw'>('rendered');
  const [copied, setCopied] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['wiki', 'document', documentId],
    queryFn: () => api.wikiDocument(documentId),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'parsing' || s === 'digesting' || s === 'uploaded' ? 5_000 : false;
    },
  });

  const reprocessMutation = useMutation({
    mutationFn: (id: string) => api.wikiDocumentReprocess(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wiki', 'document', documentId] });
      queryClient.invalidateQueries({ queryKey: ['wiki', 'documents'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'documents'] });
    },
  });

  const redigestMutation = useMutation({
    mutationFn: (id: string) => api.wikiDocumentRedigest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wiki', 'document', documentId] });
      queryClient.invalidateQueries({ queryKey: ['wiki', 'documents'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'documents'] });
    },
    onError: (err) => {
      window.alert(`LLM 정리 재실행 실패: ${(err as Error).message}`);
    },
  });

  const deleteImageMutation = useMutation({
    mutationFn: (filename: string) => api.adminWikiDocumentImageDelete(documentId, filename),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wiki', 'document', documentId] });
    },
    onError: (err) => {
      window.alert(`이미지 삭제 실패: ${(err as Error).message}`);
    },
  });

  if (detailQuery.isLoading) {
    return <div className="p-8 text-foreground-muted text-sm">로딩 중…</div>;
  }
  if (detailQuery.isError) {
    return (
      <div className="p-8 text-sm text-destructive">
        오류: {(detailQuery.error as Error).message}
      </div>
    );
  }
  const doc = detailQuery.data;
  if (!doc) {
    return <div className="p-8 text-foreground-muted text-sm">자료를 찾을 수 없습니다.</div>;
  }

  const md = doc.parsedMarkdown ?? '';
  const isInProgress = doc.status === 'parsing' || doc.status === 'digesting';
  const lastUpdateMs = doc.updatedAt ? new Date(doc.updatedAt).getTime() : null;
  const stalledForMs = isInProgress && lastUpdateMs ? Date.now() - lastUpdateMs : null;
  const looksStuck = stalledForMs !== null && stalledForMs > STUCK_THRESHOLD_MS;

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <Link
        to={backTo}
        className="mb-3 inline-flex items-center gap-1 text-xs text-foreground-muted hover:text-accent"
      >
        <ArrowLeft className="h-3 w-3" />
        자료 목록
      </Link>

      <header className="mb-6">
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">
            <FileText className="inline h-5 w-5 mr-2 -mt-1" />
            {doc.originalName}
          </h1>
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[doc.status]}`}
          >
            {STATUS_LABEL[doc.status]}
          </span>
        </div>
        <div className="mt-2 flex flex-wrap gap-3 text-xs text-foreground-faint">
          <span>.{doc.ext}</span>
          <span>{formatBytes(doc.sizeBytes)}</span>
          <span>{doc.parsedChars !== null ? `${doc.parsedChars.toLocaleString()}자` : '-'}</span>
          {doc.categorySlug ? <span>카테고리: {doc.categorySlug}</span> : null}
          <span>업로드: {new Date(doc.createdAt).toLocaleString('ko-KR')}</span>
          {doc.parsedAt ? (
            <span>변환: {new Date(doc.parsedAt).toLocaleString('ko-KR')}</span>
          ) : null}
        </div>
        {doc.status === 'failed' && doc.error ? (
          <div className="mt-3 rounded bg-destructive/10 px-3 py-2 text-xs text-destructive">
            오류: {doc.error}
          </div>
        ) : null}
        {doc.digestSummary?.imageUploadFailed && doc.digestSummary.imageUploadFailed > 0 ? (
          <div className="mt-3 flex items-start gap-2 rounded bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              이미지 업로드 일부 실패: {doc.digestSummary.imageUploadFailed}/
              {doc.digestSummary.imageUploadTotal ?? '?'}개. 마크다운에 깨진 이미지 링크가 남아있을
              수 있습니다. {isAdmin ? '재처리 권장.' : '관리자에게 재처리를 요청하세요.'}
            </span>
          </div>
        ) : null}
        {doc.digestSummary?.totalChunks ? (
          <div className="mt-2 text-xs text-foreground-muted">
            청크 {doc.digestSummary.processedChunks ?? 0}/{doc.digestSummary.totalChunks} 처리
            {doc.digestSummary.truncatedAtChunkCap && doc.digestSummary.allChunks
              ? ` (전체 ${doc.digestSummary.allChunks} 중 cap 적용)`
              : ''}
          </div>
        ) : null}
        {doc.digestSummary?.pages.length ? (
          <div className="mt-2 text-xs">
            <span className="text-foreground-muted">생성/갱신된 페이지: </span>
            {doc.digestSummary.pages.map((p, i) => (
              <span key={p.slug}>
                {i > 0 ? ', ' : ''}
                {doc.externalUrl ? (
                  <a
                    href={doc.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline font-medium"
                  >
                    {p.slug}
                  </a>
                ) : (
                  <Link
                    to="/wikis/p/$slug"
                    params={{ slug: p.slug }}
                    className="text-accent hover:underline font-medium"
                  >
                    {p.slug}
                  </Link>
                )}
                <span className="ml-1 text-foreground-faint">
                  ({p.action === 'created' ? '신규' : '갱신'})
                </span>
              </span>
            ))}
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-2">
          <a
            href={api.wikiDocumentDownloadUrl(doc.id)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs hover:border-accent"
          >
            <Download className="h-3 w-3" />
            원본 다운로드
          </a>
          {isAdmin ? (
            <>
              <button
                type="button"
                onClick={() => reprocessMutation.mutate(doc.id)}
                disabled={reprocessMutation.isPending}
                title={
                  isInProgress
                    ? '진행 중인 작업을 무시하고 처음부터 다시 처리합니다.'
                    : '이 자료를 다시 변환·정리합니다 (이미지 추출도 다시).'
                }
                className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs hover:border-accent disabled:opacity-50"
              >
                <RotateCcw className="h-3 w-3" />
                {isInProgress ? '강제 재처리' : '재처리'}
              </button>
              <button
                type="button"
                onClick={() => redigestMutation.mutate(doc.id)}
                disabled={redigestMutation.isPending || !doc.parsedMarkdown}
                title="변환은 그대로 두고 LLM 정리만 다시 실행합니다. 삭제한 이미지는 그대로 유지."
                className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs hover:border-accent disabled:opacity-50"
              >
                <RotateCcw className="h-3 w-3" />
                LLM 정리만 다시
              </button>
            </>
          ) : null}
        </div>
        {looksStuck ? (
          <div className="mt-3 flex items-start gap-2 rounded bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              마지막 진행으로부터 {Math.round((stalledForMs ?? 0) / 60000)}분 이상 변동이 없습니다.
              서버를 재시작했거나 작업이 멈춰 있을 수 있습니다.
              {isAdmin ? ' 강제 재처리 버튼을 눌러 다시 시도하세요.' : ' 관리자에게 문의하세요.'}
            </span>
          </div>
        ) : null}
      </header>

      {/* 토글 */}
      <div className="mb-3 flex items-center justify-between gap-3 border-b border-border pb-2">
        <div className="inline-flex rounded-md border border-border bg-surface p-0.5 text-xs">
          <button
            type="button"
            onClick={() => setView('rendered')}
            className={`inline-flex items-center gap-1 rounded px-3 py-1 ${
              view === 'rendered'
                ? 'bg-accent text-accent-foreground'
                : 'text-foreground-muted hover:text-foreground'
            }`}
          >
            <Eye className="h-3 w-3" />
            렌더링
          </button>
          <button
            type="button"
            onClick={() => setView('raw')}
            className={`inline-flex items-center gap-1 rounded px-3 py-1 ${
              view === 'raw'
                ? 'bg-accent text-accent-foreground'
                : 'text-foreground-muted hover:text-foreground'
            }`}
          >
            <Code2 className="h-3 w-3" />
            원본 마크다운
          </button>
        </div>
        {view === 'raw' && md ? (
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(md).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              });
            }}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:border-accent"
          >
            <Copy className="h-3 w-3" />
            {copied ? '복사됨!' : '복사'}
          </button>
        ) : null}
      </div>

      <p className="mb-3 text-xs text-foreground-faint">
        LLM 이 자료를 위키로 정리하기 위해 변환한 마크다운 본문입니다 (위키 페이지가 아님 — 위키
        페이지는 LLM 이 이 본문을 요약·재구성한 결과).
      </p>

      {!md ? (
        <div className="rounded-md border border-border bg-surface p-8 text-center text-sm text-foreground-muted">
          아직 변환되지 않았거나 변환 실패. 상태: <span className="font-mono">{doc.status}</span>
        </div>
      ) : view === 'rendered' ? (
        <div className="rounded-md border border-border bg-surface p-5 text-sm">
          <Markdown
            content={md}
            onImageDelete={isAdmin ? (f) => deleteImageMutation.mutate(f) : undefined}
          />
        </div>
      ) : (
        <pre className="overflow-x-auto rounded-md border border-border bg-code-bg p-4 font-mono text-[12px] leading-6 whitespace-pre-wrap break-words">
          {md}
        </pre>
      )}
    </div>
  );
}
